import { Loader2, Server, AlertTriangle, ArrowUpRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from "@/components/ui/responsive-modal";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getDiscordIconUrl } from "@/utils/discord";
import { useClaimGuild, isClaimGuildError } from "@/lib/api/guilds";
import { useDashboardMe } from "@/lib/api/dashboard";
import { useSubscription } from "@/lib/api/subscription";
import type { ModalComponentProps } from "@/lib/modal";
import type { DiscordGuild } from "@/lib/api/types";
import { toast } from "sonner";

export interface ClaimGuildModalProps {
  guild: DiscordGuild;
}

export function ClaimGuildModal({
  guild,
  onClose,
}: ClaimGuildModalProps & ModalComponentProps) {
  const claimGuild = useClaimGuild();
  const { data: meData } = useDashboardMe();

  const ownerTenant = meData?.data?.tenants.find((t) => t.role === "owner");
  const tenantId = ownerTenant?.id ?? "";

  const { data: subscriptionData } = useSubscription(tenantId);

  const currentGuilds = subscriptionData?.data?.usage.guilds ?? 0;
  const maxGuilds = subscriptionData?.data?.limits.maxGuildsPerApplication ?? 1;
  const isAtLimit = currentGuilds >= maxGuilds;

  const applicationId = guild.applicationId;

  const handleClaim = async () => {
    if (!applicationId) {
      toast.error("Unable to claim server", {
        description: "Application information is missing.",
      });
      return;
    }

    try {
      const result = await claimGuild.mutateAsync({
        discordGuildId: guild.id,
        applicationId,
      });

      if (result.data.alreadyOwned) {
        toast.info("Server already claimed", {
          description: "You already have access to this server.",
        });
      } else {
        toast.success("Server claimed!", {
          description: `You now have access to manage ${guild.name}.`,
        });
      }

      onClose();
    } catch (error) {
      if (isClaimGuildError(error)) {
        const data = error.response?.data;
        switch (data?.code) {
          case "GUILD_LIMIT_REACHED":
            toast.error("Server limit reached", {
              description: `You have reached your limit of ${data.max} servers. Upgrade your plan to claim more.`,
            });
            break;
          case "ALREADY_CLAIMED":
            toast.error("Server already claimed", {
              description: "This server is already managed by another team.",
            });
            break;
          case "NO_DISCORD_PERMISSION":
            toast.error("Permission denied", {
              description:
                "You must be an administrator of this Discord server to claim it.",
            });
            break;
          case "GUILD_NOT_FOUND":
            toast.error("Server not found", {
              description: "The bot is not in this server. Add the bot first.",
            });
            break;
          default:
            toast.error("Failed to claim server", {
              description: data?.error ?? "An unexpected error occurred.",
            });
        }
      } else {
        toast.error("Failed to claim server", {
          description:
            error instanceof Error
              ? error.message
              : "An unexpected error occurred.",
        });
      }
    }
  };

  return (
    <>
      <ResponsiveModalHeader>
        <ResponsiveModalTitle className="flex items-center gap-2">
          <Server className="size-5 text-rose-500" />
          Claim Server
        </ResponsiveModalTitle>
        <ResponsiveModalDescription>
          Take ownership of this server to manage it from the dashboard.
        </ResponsiveModalDescription>
      </ResponsiveModalHeader>

      <div className="space-y-4 py-4">
        <div className="flex items-center gap-3 rounded-lg border p-3">
          <Avatar className="size-12">
            <AvatarImage
              src={getDiscordIconUrl(guild.id, guild.icon)}
              alt={guild.name}
            />
            <AvatarFallback className="text-sm">
              {guild.name.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{guild.name}</p>
            <p className="text-muted-foreground text-sm">
              {guild.isOwner
                ? "You are the Discord owner"
                : "You have admin permissions"}
            </p>
          </div>
        </div>

        <div className="bg-muted/50 flex items-center justify-between rounded-lg border px-3 py-2 text-sm">
          <span className="text-muted-foreground">Server Usage</span>
          <span className={isAtLimit ? "text-destructive font-medium" : ""}>
            {currentGuilds} / {maxGuilds}
          </span>
        </div>

        {isAtLimit && (
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
              <div className="text-sm">
                <p className="font-medium text-amber-600 dark:text-amber-400">
                  Server limit reached
                </p>
                <p className="text-muted-foreground mt-1">
                  Upgrade your plan to claim more servers or remove an existing
                  server first.
                </p>
                {ownerTenant && (
                  <Button
                    variant="link"
                    size="sm"
                    className="mt-2 h-auto p-0 text-amber-600 dark:text-amber-400"
                    asChild
                  >
                    <Link
                      to="/dashboard/teams/$tenantId"
                      params={{ tenantId: ownerTenant.id }}
                      search={{ tab: "subscription" }}
                    >
                      View subscription
                      <ArrowUpRight className="ml-1 size-3" />
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <ResponsiveModalFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={claimGuild.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={handleClaim}
          disabled={isAtLimit || claimGuild.isPending || !applicationId}
        >
          {claimGuild.isPending ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              Claiming...
            </>
          ) : (
            "Claim Server"
          )}
        </Button>
      </ResponsiveModalFooter>
    </>
  );
}

ClaimGuildModal.contentClassName = "sm:max-w-md";
ClaimGuildModal.displayName = "ClaimGuildModal";
